import { ChevronDown, Plus, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getAllRole } from "@/api/role";
import { getUsersWithRoles, UserWithRoleType } from "@/api/role-user";
import toast from "react-hot-toast";

import { InputAccessConfig, InputAccessRule } from "@/types/input-access";
import { RoleType } from "@/types/role";
import {
  AccessAction,
  AccessRuleMode,
  AccessRuleStatus
} from "@/types/table-access";

import { InputAccessManager } from "@/utils/input-access-manager";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const togglePrimitive = <T extends string | number>(
  items: T[],
  item: T
): T[] => {
  return items.includes(item)
    ? items.filter((x) => x !== item)
    : [...items, item];
};

const normalizeText = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

interface InputAccessModalProps {
  isOpen: boolean;
  inputId: string;
  input: HTMLElement | null;
  onClose: () => void;
  title?: string;
}

export const InputAccessModal: React.FC<InputAccessModalProps> = ({
  isOpen,
  inputId,
  input,
  onClose,
  title = "مدیریت دسترسی ورودی"
}) => {
  const [config, setConfig] = useState<InputAccessConfig>(() => {
    if (!input) return InputAccessManager.createConfig(inputId);

    const existingData =
      input.getAttribute("data-access") ??
      input.getAttribute("data-access-control");
    const parsed = InputAccessManager.deserialize(existingData);
    return parsed
      ? { ...parsed, inputId }
      : InputAccessManager.createConfig(inputId);
  });

  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleType[]>([]);
  const [users, setUsers] = useState<UserWithRoleType[]>([]);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const existingData =
      input?.getAttribute("data-access") ??
      input?.getAttribute("data-access-control") ??
      null;
    const parsed = InputAccessManager.deserialize(existingData);

    setConfig(
      parsed ? { ...parsed, inputId } : InputAccessManager.createConfig(inputId)
    );
    setExpandedRuleId(null);
    setRoleSearch("");
    setUserSearch("");
  }, [isOpen, input, inputId]);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const loadRoles = async () => {
      setIsRolesLoading(true);
      try {
        const response = await getAllRole({ page: 1, size: 1000 });
        if (!isCancelled) {
          setRoles(response?.data ?? []);
        }
      } catch {
        if (!isCancelled) {
          setRoles([]);
          toast.error("دریافت لیست نقش‌ها ناموفق بود");
        }
      } finally {
        if (!isCancelled) {
          setIsRolesLoading(false);
        }
      }
    };

    loadRoles();

    return () => {
      isCancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const loadUsers = async () => {
      setIsUsersLoading(true);
      try {
        const response = await getUsersWithRoles({
          pageSize: 1000,
          pageNumber: 1,
          search: userSearch.trim() || undefined
        });
        if (!isCancelled) {
          setUsers(response?.data ?? []);
        }
      } catch {
        if (!isCancelled) {
          setUsers([]);
          toast.error("دریافت لیست کاربران ناموفق بود");
        }
      } finally {
        if (!isCancelled) {
          setIsUsersLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, userSearch]);

  const filteredRoles = useMemo(() => {
    const query = normalizeText(roleSearch).toLowerCase();
    if (!query) return roles;

    return roles.filter((role) => {
      const labelFa = role.displayNameFa ?? role.nameFa;
      const labelEn = role.displayNameEn ?? role.nameEn;
      const haystack = `${labelFa} ${labelEn}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [roles, roleSearch]);

  const resolveUserLabel = useCallback((user: UserWithRoleType) => {
    return user.fullName?.trim() || user.userName || String(user.userId);
  }, []);

  const saveConfiguration = useCallback(() => {
    if (!input) return;

    const serialized = InputAccessManager.serialize(config);
    input.setAttribute("data-access", serialized);
    toast.success("تنظیمات دسترسی ذخیره شد");
    onClose();
  }, [config, input, onClose]);

  const handleAddRule = useCallback(() => {
    setConfig((prev) => {
      const newRule = InputAccessManager.createRule(
        `قانون ${prev.rules.length + 1}`
      );
      setExpandedRuleId(newRule.id);
      return InputAccessManager.addRule(prev, newRule);
    });
  }, []);

  const handleDeleteRule = useCallback((ruleId: string) => {
    setConfig((prev) => InputAccessManager.removeRule(prev, ruleId));
    setExpandedRuleId((prev) => (prev === ruleId ? null : prev));
    toast.success("قانون حذف شد");
  }, []);

  const handleUpdateRule = useCallback(
    (ruleId: string, updates: Partial<InputAccessRule>) => {
      setConfig((prev) => InputAccessManager.updateRule(prev, ruleId, updates));
    },
    []
  );

  const toggleRoleForRule = useCallback((ruleId: string, roleId: number) => {
    setConfig((prev) => {
      const rule = InputAccessManager.getRule(prev, ruleId);
      if (!rule) return prev;

      return InputAccessManager.updateRule(prev, ruleId, {
        allRoles: false,
        roleIds: togglePrimitive(rule.roleIds, roleId)
      });
    });
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-right text-lg font-bold">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Button
            onClick={handleAddRule}
            variant="outline"
            className="w-full gap-2"
          >
            <Plus size={18} />
            <span>افزودن قانون جدید</span>
          </Button>

          <div className="space-y-2">
            {config.rules.map((rule) => (
              <div key={rule.id} className="rounded-lg border p-4">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex flex-1 items-center gap-3">
                    <button
                      onClick={() =>
                        setExpandedRuleId(
                          expandedRuleId === rule.id ? null : rule.id
                        )
                      }
                      className="rounded p-1 hover:bg-gray-100"
                    >
                      <ChevronDown
                        size={20}
                        className={`transition-transform ${
                          expandedRuleId === rule.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <div className="flex-1">
                      <Input
                        value={rule.name}
                        onChange={(e) =>
                          handleUpdateRule(rule.id, { name: e.target.value })
                        }
                        className="text-right font-semibold"
                        placeholder="نام قانون"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteRule(rule.id)}
                    className="rounded p-2 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {expandedRuleId === rule.id && (
                  <div className="space-y-4 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">وضعیت:</label>
                      <select
                        value={
                          rule.status === AccessRuleStatus.ACTIVE
                            ? "active"
                            : "inactive"
                        }
                        onChange={(e) =>
                          handleUpdateRule(rule.id, {
                            status:
                              e.target.value === "active"
                                ? AccessRuleStatus.ACTIVE
                                : AccessRuleStatus.INACTIVE
                          })
                        }
                        className="rounded-lg border px-3 py-2 text-sm"
                      >
                        <option value="active">فعال</option>
                        <option value="inactive">غیرفعال</option>
                      </select>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">عمل:</label>
                      <select
                        value={rule.action}
                        onChange={(e) =>
                          handleUpdateRule(rule.id, {
                            action: e.target.value as AccessAction
                          })
                        }
                        className="rounded-lg border px-3 py-2 text-sm"
                      >
                        <option value={AccessAction.SHOW}>نمایش</option>
                        <option value={AccessAction.HIDE}>مخفی</option>
                      </select>
                    </div>

                    <div className="border-t pt-4">
                      <label className="mb-3 block text-sm font-medium">
                        شرط اعمال قانون:
                      </label>
                      <Tabs
                        value={rule.mode}
                        onValueChange={(value) =>
                          handleUpdateRule(rule.id, {
                            mode: value as AccessRuleMode
                          })
                        }
                        dir="rtl"
                      >
                        <TabsList className="w-full rounded-lg bg-slate-100 p-1">
                          <TabsTrigger
                            value={AccessRuleMode.ROLE}
                            className="flex-1 rounded-md data-[state=active]:bg-white"
                          >
                            براساس نقش
                          </TabsTrigger>
                          <TabsTrigger
                            value={AccessRuleMode.USER}
                            className="flex-1 rounded-md data-[state=active]:bg-white"
                          >
                            براساس کاربر
                          </TabsTrigger>
                          <TabsTrigger
                            value={AccessRuleMode.QUERY}
                            className="flex-1 rounded-md data-[state=active]:bg-white"
                          >
                            براساس کوئری
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent
                          value={AccessRuleMode.ROLE}
                          className="pt-4"
                        >
                          <div className="space-y-3">
                            <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
                              <span className="text-sm font-medium">همه</span>
                              <Checkbox
                                checked={rule.allRoles}
                                onCheckedChange={(checked) =>
                                  handleUpdateRule(rule.id, {
                                    allRoles: checked === true
                                  })
                                }
                              />
                            </label>

                            <div className="flex items-center justify-between gap-3">
                              <label className="text-sm font-medium">
                                نقش‌ها
                              </label>
                              <span className="text-xs text-slate-500">
                                {rule.allRoles
                                  ? "همه نقش‌ها"
                                  : rule.roleIds.length
                                    ? `${rule.roleIds.length} مورد انتخاب شده`
                                    : "بدون انتخاب"}
                              </span>
                            </div>

                            <Input
                              value={roleSearch}
                              onChange={(e) => setRoleSearch(e.target.value)}
                              placeholder="جستجو نقش..."
                              className="text-right"
                              disabled={rule.allRoles}
                            />

                            <ScrollArea
                              className={`h-56 rounded-lg border border-slate-200 bg-white p-3 ${
                                rule.allRoles
                                  ? "pointer-events-none opacity-60"
                                  : ""
                              }`}
                            >
                              <div className="space-y-2">
                                {isRolesLoading ? (
                                  <div className="py-6 text-center text-sm text-slate-500">
                                    در حال بارگذاری نقش‌ها...
                                  </div>
                                ) : filteredRoles.length ? (
                                  filteredRoles.map((role) => {
                                    const labelFa =
                                      role.displayNameFa ?? role.nameFa;
                                    const isChecked = rule.roleIds.includes(
                                      role.id
                                    );

                                    return (
                                      <label
                                        key={role.id}
                                        className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 px-3 py-2 transition-colors hover:border-primary/60 hover:bg-primary/5"
                                      >
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={() =>
                                            toggleRoleForRule(rule.id, role.id)
                                          }
                                          className="mt-0.5"
                                        />
                                        <span className="flex-1 text-sm">
                                          <span className="block font-medium text-slate-700">
                                            {labelFa}
                                          </span>
                                          {role.description && (
                                            <span className="mt-1 block text-xs text-slate-500">
                                              {role.description}
                                            </span>
                                          )}
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="py-6 text-center text-sm text-slate-500">
                                    نقشی یافت نشد
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        </TabsContent>

                        <TabsContent
                          value={AccessRuleMode.USER}
                          className="pt-4"
                        >
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <label className="text-sm font-medium">
                                کاربران
                              </label>
                              <span className="text-xs text-slate-500">
                                {rule.userIds.length
                                  ? `${rule.userIds.length} مورد انتخاب شده`
                                  : "بدون انتخاب"}
                              </span>
                            </div>

                            <Input
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              placeholder="جستجو کاربر..."
                              className="text-right"
                            />

                            <ScrollArea className="h-56 rounded-lg border border-slate-200 bg-white p-3">
                              <div className="space-y-2">
                                {isUsersLoading ? (
                                  <div className="py-6 text-center text-sm text-slate-500">
                                    در حال بارگذاری کاربران...
                                  </div>
                                ) : users.length ? (
                                  users.map((user) => {
                                    const isChecked = rule.userIds.includes(
                                      user.userId
                                    );

                                    return (
                                      <label
                                        key={user.userId}
                                        className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 px-3 py-2 transition-colors hover:border-primary/60 hover:bg-primary/5"
                                      >
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={() =>
                                            handleUpdateRule(rule.id, {
                                              userIds: togglePrimitive(
                                                rule.userIds,
                                                user.userId
                                              )
                                            })
                                          }
                                          className="mt-0.5"
                                        />
                                        <span className="flex-1 text-sm">
                                          <span className="block font-medium text-slate-700">
                                            {resolveUserLabel(user)}
                                          </span>
                                          <span className="mt-1 block text-xs text-slate-500">
                                            {user.userName}
                                            {user.roleNameFa
                                              ? ` - ${user.roleNameFa}`
                                              : ""}
                                          </span>
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="py-6 text-center text-sm text-slate-500">
                                    کاربری یافت نشد
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        </TabsContent>

                        <TabsContent
                          value={AccessRuleMode.QUERY}
                          className="pt-4"
                        >
                          <div className="space-y-3">
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                              کوئری باید خروجی{" "}
                              <span className="font-mono">true/false</span>
                              داشته باشد. وقتی{" "}
                              <span className="font-mono">true</span> شد، این
                              قانون روی ورودی اعمال می‌شود.
                              <div className="mt-1 font-mono">
                                مثال: roleId == 3 && userId != 0
                              </div>
                            </div>

                            <Textarea
                              value={rule.query}
                              onChange={(e) =>
                                handleUpdateRule(rule.id, {
                                  query: e.target.value
                                })
                              }
                              className="min-h-[120px] font-mono text-sm"
                              placeholder="کوئری را وارد کنید"
                            />
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>

                    {!InputAccessManager.isValidRule(rule) && (
                      <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                        ⚠️ لطفاً تمام فیلدهای ضروری را پر کنید
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {config.rules.length === 0 && (
              <div className="py-8 text-center text-gray-400">
                هنوز قانونی تعریف نشده. برای شروع روی دکمه فوق کلیک کنید.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            لغو
          </Button>
          <Button onClick={saveConfiguration}>
            <span>ذخیره تنظیمات</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
